import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Users } from 'lucide-react';

const events = [
  {
    id: 'retail-investors-expo',
    title: 'India Retail Investors Expo',
    category: 'Expo',
    description: 'India\'s premier platform connecting retail investors with leading financial institutions and investment opportunities.',
    image: 'https://images.unsplash.com/photo-1768396855390-0728fa9c21e1?crop=entropy&cs=srgb&fm=jpg&q=85',
    date: 'February 15-16, 2025',
    location: 'HITEX, Hyderabad',
    attendees: '10,000+',
    link: '/events/retail-investors-expo'
  },
  {
    id: 'finrun',
    title: 'The FinRun',
    category: 'Live Event',
    description: 'A unique marathon combining fitness with financial awareness, bringing together runners and finance enthusiasts.',
    image: 'https://images.unsplash.com/photo-1746046489457-9628dc3b8a1f?crop=entropy&cs=srgb&fm=jpg&q=85',
    date: 'March 9, 2025',
    location: 'Hyderabad',
    attendees: '5,000+',
    link: '/events/finrun'
  }
];

const pastEvents = [
  {
    title: 'Tech Innovation Summit 2024',
    date: 'November 2024',
    location: 'Bangalore',
    image: 'https://images.unsplash.com/photo-1740727263760-d220d5a90d9f?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  {
    title: 'Corporate Excellence Awards',
    date: 'October 2024',
    location: 'Mumbai',
    image: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  {
    title: 'Finance & Investment Expo 2024',
    date: 'September 2024',
    location: 'Hyderabad',
    image: 'https://images.pexels.com/photos/7567438/pexels-photo-7567438.jpeg'
  }
];

export default function EventsPage() {
  return (
    <div className="page-transition" data-testid="events-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-navy overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=2000&q=80"
              alt="Background"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Our Events
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">
                Events That <span className="text-gold">Define Industries</span>
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed">
                From large-scale expos to dynamic marathons, we create events that bring people together and create lasting impact.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Upcoming Events */}
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Upcoming Events
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy">
                Don't Miss Out
              </h2>
            </motion.div>

            <div className="space-y-12">
              {events.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`upcoming-event-${event.id}`}
                >
                  <Link to={event.link} className="group block">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                      <div className={`relative h-[350px] overflow-hidden ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                        <img
                          src={event.image}
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute top-6 left-6">
                          <span className="bg-gold text-navy text-sm font-semibold px-4 py-2 uppercase tracking-wider">
                            {event.category}
                          </span>
                        </div>
                      </div>

                      <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                        <h3 className="font-heading text-2xl md:text-3xl lg:text-4xl text-navy mb-4 group-hover:text-gold transition-colors">
                          {event.title}
                        </h3>
                        <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                          {event.description}
                        </p>

                        <div className="flex flex-wrap gap-6 text-muted-foreground mb-6">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-gold" />
                            {event.date}
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-gold" />
                            {event.location}
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-gold" />
                            {event.attendees}
                          </div>
                        </div>

                        <div className="inline-flex items-center gap-2 text-navy font-semibold uppercase tracking-wider group-hover:text-gold group-hover:gap-4 transition-all">
                          Learn More
                          <ArrowRight className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Past Events */}
        <section className="py-20 lg:py-32 bg-muted">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Track Record
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy mb-6">
                Past Events
              </h2>
              <p className="text-muted-foreground text-lg">
                A glimpse into the successful events we've organized over the years.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {pastEvents.map((event, index) => (
                <motion.div
                  key={event.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white overflow-hidden hover-lift"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl text-navy mb-2">{event.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-gold" />
                        {event.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-gold" />
                        {event.location}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-24 bg-navy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-3xl md:text-4xl text-white mb-6"
            >
              Want to Participate or Sponsor?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-white/80 text-lg mb-10 max-w-2xl mx-auto"
            >
              Contact us to learn about exhibitor booths, sponsorship packages, and participation opportunities.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Get In Touch
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
